import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClientLayout from "@/components/ClientLayout";
import api, { API } from "@/lib/api";
import { goToOffers } from "@/lib/offers";
import { Send, Sparkles, RotateCcw, Wand2, Clock, Check, X, HelpCircle, HeartHandshake, Ticket, RefreshCw, Info, MessageSquare, AlertTriangle, UserCog } from "lucide-react";
import { toast } from "sonner";
import { GlossaryTerm } from "@/components/GlossaryTerm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function UpsellChatCard({ m, onDismiss }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const Icon = m.type === "premium_upgrade" ? HeartHandshake : m.type === "rdv_unique" ? Ticket : RefreshCw;
  const act = async () => {
    if (m.cta_action === "navigate") { navigate(m.cta_target); return; }
    // CTA de l'assistant -> page de vente (comparaison). L'assistant ne déclenche JAMAIS de paiement direct.
    goToOffers(navigate, { source: "assistant", returnTo: "/assistant", renewal: !!m.renewal });
  };
  return (
    <div data-testid="chat-upsell-card" className="flex gap-3 fade-up">
      <span className="mt-1 w-7 h-7 shrink-0 rounded-full bg-sage/15 flex items-center justify-center"><Icon size={13} className="text-sage" /></span>
      <div className="w-full max-w-[88%] rounded-2xl rounded-tl-sm border border-sage/25 bg-gradient-to-br from-sage/10 via-brandblue/5 to-transparent px-4 py-3">
        <div className="text-[13px] font-medium text-obsidian">{m.title}</div>
        <p className="mt-0.5 text-[13px] text-obsidian/65 leading-snug">{m.message}</p>
        <div className="mt-2.5 flex items-center gap-2">
          <button data-testid="chat-upsell-cta" onClick={act} disabled={loading}
            className="rounded-full bg-obsidian text-white text-[12px] font-semibold px-3.5 py-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60">
            {loading ? "…" : m.cta_label}
          </button>
          <button data-testid="chat-upsell-dismiss" onClick={() => onDismiss(m.type)}
            className="text-[12px] text-obsidian/45 hover:text-obsidian/70 px-2 py-1 transition-colors">Plus tard</button>
        </div>
      </div>
    </div>
  );
}

function EnginePrescriptionCard() {
  // Second cerveau legacy (/coaching-engine/me/prescribe) retiré du parcours : la route est démontée (404).
  return null;
}

function _EnginePrescriptionCardLegacy() {
  const [presc, setPresc] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => Promise.resolve(null).then((r) => setPresc(r));
  useEffect(() => { load(); }, []);
  if (!presc) return null;
  const p = presc.prescription || {};
  const famLabel = (presc.famille || "").replace(/_/g, " ");
  return (
    <div data-testid="engine-prescription" className="rounded-2xl border border-sage/30 bg-sage/8 p-4 mb-4">
      <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wide text-sage uppercase"><HelpCircle size={13} /> Pourquoi cette séance ?</div>
      <div className="mt-1.5 text-[14px] text-obsidian/90 leading-snug">{presc.explication_utilisateur}</div>
      <button data-testid="engine-toggle" onClick={() => setOpen(!open)} className="mt-2 text-[12px] text-obsidian/60 underline underline-offset-2">{open ? "Masquer" : "Détails"}</button>
      {open && (
        <div className="mt-2 text-[13px] text-obsidian/70 space-y-1">
          <div>Besoin identifié : {presc.besoin}</div>
          <div>Type de travail : <GlossaryTerm term={famLabel}>{famLabel}</GlossaryTerm></div>
          {p.nom && <div>Séance proposée : {p.nom}</div>}
          <div>Décision de sécurité : {presc.decision?.level_name}</div>
        </div>
      )}
    </div>
  );
}

// Statuts réels pilotés par le système (jamais par le modèle).
const STATUS_META = {
  conseil: { label: "Conseil — aucun changement du programme", cls: "text-obsidian/45 border-border bg-obsidian/[0.03]", icon: Info },
  proposed: { label: "Ajustement proposé — en attente de confirmation", cls: "text-amber-700 border-amber-200 bg-amber-50", icon: Clock },
  coach: { label: "Demande coach — analyse nécessaire", cls: "text-brandblue border-brandblue/25 bg-brandblue/[0.06]", icon: UserCog },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <div data-testid={`mai-status-${status}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-semibold ${meta.cls}`}>
      <Icon size={11} /> {meta.label}
    </div>
  );
}

function ProposalCard({ m, onConfirm, onReject, busy }) {
  return (
    <div data-testid={`proposal-card-${m.adaptation_id}`} className="mt-2 rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3">
      {Array.isArray(m.proposed_changes) && m.proposed_changes.length > 0 && (
        <ul className="space-y-1">
          {m.proposed_changes.map((c, i) => (
            <li key={i} className="text-[13px] text-obsidian/80 flex gap-1.5"><span className="opacity-40">•</span>{c}</li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-[12px] text-amber-800/80">Cette proposition n'a pas encore modifié ton programme.</p>
      <div className="mt-2.5 flex items-center gap-2">
        <button data-testid={`confirm-proposal-${m.adaptation_id}`} onClick={() => onConfirm(m.adaptation_id)} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full bg-obsidian text-white text-[12px] font-semibold px-3.5 py-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60">
          <Check size={13} /> Confirmer
        </button>
        <button data-testid={`reject-proposal-${m.adaptation_id}`} onClick={() => onReject(m.adaptation_id)} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white text-[12px] font-medium px-3.5 py-2 text-obsidian/70 hover:text-obsidian transition-colors disabled:opacity-60">
          <X size={13} /> Annuler
        </button>
      </div>
    </div>
  );
}

function AdaptationCard({ m, onUndo }) {
  const status = m.status || "applied";
  const meta = {
    applied: { label: "Programme modifié — modification enregistrée", cls: "bg-sage/12 border-sage/30 text-sage", icon: Wand2 },
    pending_coach: { label: "EN ATTENTE DE VALIDATION COACH", cls: "bg-amber-50 border-amber-200 text-amber-700", icon: Clock },
    reverted: { label: "ADAPTATION ANNULÉE", cls: "bg-obsidian/5 border-border text-obsidian/40", icon: RotateCcw },
    rejected: { label: "REFUSÉE PAR LE COACH", cls: "bg-red-50 border-red-200 text-red-600", icon: X },
  }[status] || { label: "ADAPTATION IA", cls: "bg-sage/12 border-sage/30 text-sage", icon: Wand2 };
  const Icon = meta.icon;
  return (
    <div data-testid={`adaptation-card-${m.adaptation_id}`} className="flex justify-center fade-up">
      <div className={`w-full max-w-[92%] rounded-2xl border px-4 py-3 ${meta.cls}`}>
        <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-wide">
          <Icon size={12} /> {meta.label}
          {m.ts && <span className="ml-auto font-normal opacity-60">{new Date(m.ts).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
        </div>
        {m.summary && <div className={`mt-1.5 text-[14px] leading-snug ${status === "reverted" ? "line-through opacity-60" : "text-obsidian/90"}`}>{m.summary}</div>}
        {Array.isArray(m.changes) && m.changes.length > 0 && (
          <ul className="mt-1.5 space-y-0.5">
            {m.changes.map((c, i) => (
              <li key={i} className="text-[12.5px] text-obsidian/70 flex gap-1.5"><span className="opacity-40">•</span>{c}</li>
            ))}
          </ul>
        )}
        {status === "applied" && (
          <button data-testid={`undo-adaptation-${m.adaptation_id}`} onClick={() => onUndo(m.adaptation_id)}
            className="mt-2.5 inline-flex items-center gap-1.5 text-[12px] font-medium text-obsidian/70 hover:text-obsidian rounded-full border border-border bg-white px-3 py-1 transition-colors">
            <RotateCcw size={12} /> Annuler cette adaptation
          </button>
        )}
      </div>
    </div>
  );
}

const INTRO_TEXT = `Je suis MAI, ton assistant de programmation running. Je peux t'aider à comprendre tes séances, tes allures et la logique de ton programme. Je peux aussi te proposer des ajustements ponctuels selon ta fatigue, tes contraintes ou une séance manquée. Les changements importants — nouvel objectif, reconstruction de plusieurs semaines ou modification complète des cycles — nécessitent la validation d'un coach. Je te préciserai toujours si je te donne un conseil, si je te propose un ajustement ou si ton programme a réellement été modifié.`;

function CapabilitiesPanel({ open, onClose }) {
  const Row = ({ items, can }) => (
    <ul className="mt-1.5 space-y-1.5">
      {items.map((t, i) => (
        <li key={i} className="flex items-start gap-2 text-[13.5px] text-obsidian/80">
          {can ? <Check size={15} className="mt-0.5 shrink-0 text-sage" /> : <X size={15} className="mt-0.5 shrink-0 text-obsidian/35" />}
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="capabilities-panel" aria-describedby={undefined} className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Ce que MAI peut faire</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-[11px] font-semibold tracking-wide text-sage uppercase">MAI peut directement</div>
            <Row can items={["Expliquer une séance", "Expliquer une allure ou une zone d'intensité", "Répondre à tes questions sur le programme", "Conseiller sur la récupération", "T'aider après une séance manquée", "Proposer un ajustement ponctuel", "Signaler une incohérence ou un risque"]} />
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-wide text-obsidian/45 uppercase">MAI ne peut pas directement</div>
            <Row items={["Diagnostiquer une blessure", "Remplacer un professionnel de santé", "Reconstruire librement plusieurs cycles (offre Essentiel)", "Affirmer qu'une modification est enregistrée si elle ne l'est pas", "Inventer un rendez-vous ou une fonction inexistante"]} />
          </div>
          <div className="rounded-xl border border-brandblue/20 bg-brandblue/[0.05] p-3">
            <div className="text-[11px] font-semibold tracking-wide text-brandblue uppercase">Avec l'accompagnement coach (Premium)</div>
            <Row can items={["Les demandes complexes sont analysées humainement", "Plusieurs semaines ou cycles peuvent être reconstruits", "Un nouvel objectif peut être intégré", "Les modifications importantes sont validées par le coach"]} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [introSeen, setIntroSeen] = useState(true);
  const [showCaps, setShowCaps] = useState(false);
  const scrollRef = useRef(null);

  const loadHistory = () => api.get("/chat/history").then((r) => { setMessages(r.data.messages || []); setIntroSeen(!!r.data.intro_seen); });
  useEffect(() => { loadHistory(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const ackIntro = async () => { setIntroSeen(true); await api.post("/chat/intro-seen").catch(() => {}); };

  const undo = async (id) => {
    try { await api.post(`/chat/adaptation/${id}/undo`); toast.success("Adaptation annulée"); await loadHistory(); }
    catch { toast.error("Impossible d'annuler"); }
  };

  const confirmProposal = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/chat/adaptation/${id}/confirm`);
      if (r.data.status === "applied") toast.success("Programme modifié — modification enregistrée");
      else toast.error(r.data.message || "La modification n'a pas pu être enregistrée.");
      await loadHistory();
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (st === 409) toast.error(detail || "Adaptation refusée par sécurité : ton programme n'a pas été modifié.", { duration: 8000 });
      else if (st === 503) toast.error(detail || "Vérification indisponible : ton programme actuel est conservé, réessaie dans un instant.");
      else toast.error("Impossible de confirmer");
      await loadHistory();
    }
    finally { setBusyId(null); }
  };

  const rejectProposal = async (id) => {
    setBusyId(id);
    try { await api.post(`/chat/adaptation/${id}/reject`); toast.success("Proposition annulée"); await loadHistory(); }
    catch { toast.error("Impossible d'annuler"); }
    finally { setBusyId(null); }
  };

  const dismissUpsell = async (type) => {
    setMessages((m) => m.filter((x) => !(x.role === "upsell" && x.type === type)));
    if (type) await api.post("/upsell/dismiss", { type }).catch(() => {});
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", pending: true }]);
    setSending(true);
    try {
      await api.post("/chat/message", { content: text });
      await loadHistory();
    } catch { toast.error("Erreur de connexion"); await loadHistory(); }
    finally { setSending(false); }
  };

  return (
    <ClientLayout fill>
      <CapabilitiesPanel open={showCaps} onClose={() => setShowCaps(false)} />
      <div className="flex flex-col flex-1 min-h-0 pt-5">
        <div className="flex items-center gap-2.5 pb-4 border-b border-border shrink-0">
          <span className="w-9 h-9 rounded-full bg-sage/15 flex items-center justify-center"><Sparkles size={16} className="text-sage" /></span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium leading-none">MAI · assistant course à pied</div>
            <div className="text-[11px] text-obsidian/50 mt-0.5">Disponible à tout moment · tu échanges avec une IA</div>
          </div>
          <button data-testid="what-mai-can-do" onClick={() => setShowCaps(true)}
            className="inline-flex items-center gap-1.5 text-[12px] text-obsidian/55 hover:text-obsidian rounded-full border border-border px-3 py-1.5 transition-colors shrink-0">
            <Info size={13} /> Ce que MAI peut faire
          </button>
        </div>

        {!introSeen && (
          <div data-testid="mai-intro-card" className="mt-3 shrink-0 rounded-2xl border border-sage/30 bg-sage/[0.06] p-4">
            <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wide text-sage uppercase"><Sparkles size={13} /> Bienvenue</div>
            <p className="mt-1.5 text-[13.5px] text-obsidian/80 leading-relaxed">{INTRO_TEXT}</p>
            <button data-testid="mai-intro-ack" onClick={ackIntro}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-obsidian text-white text-[12.5px] font-semibold px-4 py-2 hover:-translate-y-0.5 transition-transform">
              <Check size={14} /> Compris
            </button>
          </div>
        )}

        <div className="pt-3 shrink-0"><EnginePrescriptionCard /></div>

        <div ref={scrollRef} data-testid="chat-messages" className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-6 space-y-5">
          {messages.map((m, i) => (
            <div key={i} className="fade-up">
              {m.role === "adaptation" ? (
                <AdaptationCard m={m} onUndo={undo} />
              ) : m.role === "upsell" ? (
                <UpsellChatCard m={m} onDismiss={dismissUpsell} />
              ) : m.role === "assistant" ? (
                <div className="flex gap-3">
                  <span className="mt-1 w-7 h-7 shrink-0 rounded-full bg-sage/15 flex items-center justify-center"><Sparkles size={13} className="text-sage" /></span>
                  <div className="w-full max-w-[88%]">
                    {m.mai_status && <div className="mb-1.5"><StatusBadge status={m.mai_status} /></div>}
                    <div className="rounded-2xl rounded-tl-sm bg-white border border-border px-4 py-3 text-[15px] leading-relaxed text-obsidian/90">
                      {m.pending ? <span className="pulse-organic">…</span> : (m.content || "—")}
                    </div>
                    {Array.isArray(m.warning_flags) && m.warning_flags.length > 0 && (
                      <div data-testid="mai-warnings" className="mt-2 rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2">
                        {m.warning_flags.map((w, k) => (
                          <div key={k} className="flex items-start gap-1.5 text-[12.5px] text-amber-800"><AlertTriangle size={13} className="mt-0.5 shrink-0" />{w}</div>
                        ))}
                      </div>
                    )}
                    {m.mai_status === "proposed" && m.adaptation_id && (
                      <ProposalCard m={m} onConfirm={confirmProposal} onReject={rejectProposal} busy={busyId === m.adaptation_id} />
                    )}
                    {m.coach_hint && (
                      <div data-testid="mai-coach-hint" className="mt-2 rounded-xl border border-brandblue/20 bg-brandblue/[0.05] px-3 py-2 text-[12.5px] text-brandblue flex items-center gap-1.5">
                        <MessageSquare size={13} /> {m.coach_hint}
                      </div>
                    )}
                    {m.upgrade_hint && (
                      <div data-testid="mai-upgrade-hint" className="mt-2 rounded-xl border border-sage/25 bg-sage/[0.06] px-3 py-2 text-[12.5px] text-obsidian/75">{m.upgrade_hint}</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex justify-end"><div className="rounded-2xl rounded-tr-sm bg-obsidian text-white px-4 py-3 text-[15px] leading-relaxed max-w-[85%]">{m.content}</div></div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-end gap-3 pt-3 pb-3 border-t border-border shrink-0">
          <textarea data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1} placeholder="Pose ta question…"
            className="flex-1 resize-none rounded-2xl border border-border bg-white px-4 py-3 text-[15px] focus:ring-1 focus:ring-obsidian focus:outline-none" />
          <button data-testid="chat-send" onClick={send} disabled={sending || !input.trim()}
            className="w-11 h-11 shrink-0 rounded-full bg-obsidian text-white flex items-center justify-center disabled:opacity-30 hover:-translate-y-0.5 transition-transform"><Send size={17} /></button>
        </div>
      </div>
    </ClientLayout>
  );
}
